export type PresetStylingMap = Record<string, Record<string, string>>;

function normalizeStyleProperty(property: string): string {
  const trimmedProperty = property.trim();

  if (!trimmedProperty) {
    return trimmedProperty;
  }

  // Preserve CSS custom properties exactly as provided.
  if (trimmedProperty.startsWith('--')) {
    return trimmedProperty;
  }

  return trimmedProperty.replace(
    /[A-Z]/g,
    (letter) => `-${letter.toLowerCase()}`,
  );
}

export function parseStylingByPreset(
  raw: string | null | undefined,
): PresetStylingMap {
  if (!raw) {
    return {};
  }

  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    return Object.entries(parsed).reduce<PresetStylingMap>(
      (acc, [key, value]) => {
        if (value && typeof value === 'object') {
          const styleObject = Object.entries(value as Record<string, unknown>)
            .filter(([, val]) => typeof val === 'string')
            .reduce<Record<string, string>>((styleAcc, [prop, propValue]) => {
              const normalizedProperty = normalizeStyleProperty(prop);

              if (normalizedProperty) {
                styleAcc[normalizedProperty] = propValue as string;
              }

              return styleAcc;
            }, {});

          if (Object.keys(styleObject).length) {
            acc[key.toLowerCase()] = styleObject;
          }
        }
        return acc;
      },
      {},
    );
  } catch (error) {
    console.warn('Failed to parse styling-by-preset config:', error);
    return {};
  }
}

export function resolvePresetStyles(
  stylingMap: PresetStylingMap,
  preset: string | null | undefined,
): Record<string, string> {
  const allStyles = stylingMap.all ?? {};

  if (!preset) {
    return { ...allStyles };
  }

  return {
    ...allStyles,
    ...(stylingMap[preset.toLowerCase()] ?? {}),
  };
}

export function stringifyStyles(styles?: Record<string, string>): string {
  if (!styles) return '';
  return Object.entries(styles)
    .map(([prop, value]) => `${normalizeStyleProperty(prop)}:${value.trim()}`)
    .join(';');
}
